// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { SummaryDocsService } from 'src/app/modules/summary-docs/summary-docs.service';
import { Company } from 'src/app/shared/models/company/company.model';
import { IUser } from "src/app/shared/models/user/user.interface";
import { ApiService } from 'src/app/shared/services/api.service';
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Script start
 */

@Component({
  selector: "app-summary-docs-properties-dialog",
  templateUrl: "./summary-docs-properties-dialog.component.html",
  styleUrls: ["./summary-docs-properties-dialog.component.scss"]
})
export class SummaryDocsPropertiesDialogComponent
  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = true; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Edit item form
  existingNotes: any[] = [];
  qrcodeData = "";
  sortedData = [];
  properties: any[] = [];
  selectAll: boolean;
  company: Company;
  previousCompanyId: string;
  preSelectedCompany: any;
  isInit: boolean;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<SummaryDocsPropertiesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private summaryDocsService: SummaryDocsService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
    private companiesService: CompaniesService,
  ) {
    this.selectAll = this.data.selectAll;
  }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    this.isInit = true;
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    this.subscriptions.push(
      this.summaryDocsService
        .getSummaryDocsProperties()
        .subscribe(res => {
          if (res.status) {
            // Get pre selected company data
            if (this.data.selectedCompany) {
              this.getCompanyData(this.data.selectedCompany);
              this.initProperties(res.data.items);
              setTimeout(() => {
                this.dialogIsLoading = false;
                return;
              }, 500);
            } else {
              this.initCompanyForm();
              this.initProperties(res.data.items);
              this.isInit = false;
              setTimeout(() => {
                this.dialogIsLoading = false;
                return;
              }, 500);
            }
          } else {
            this.toastrService.error(res.message);
            this.isInit = false;
            setTimeout(() => {
              this.dialogIsLoading = false;
              return;
            }, 500);
          }
        })
    );

    // Get current lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * init field form
   *
   * @since 1.0.0
   */
  initProperties(items: any[]): void {
    if (items && Array.isArray(items)) {
      items.forEach((item: any) => {
        let found = false;
        if (this.data && this.data.properties && Array.isArray(this.data.properties)) {
          const val = this.data.properties.find((el: any) => el.id === item._id)
          if (typeof val !== "undefined") {
            found = true;
          }
        }
        // Prevent duplicate insert
        if (!this.properties.map(el => el.id).includes(item._id)) {
          this.properties.push({
            value: found,
            id: item._id,
            name: item.name
          });
        }
      });
    }
  }

  /**
   * Init company field form
   *
   * @since 1.0.0
   */
  initCompanyForm(preSelectedCompany?: any): void {
    if (preSelectedCompany && preSelectedCompany._id) {
      this.model["company"] = preSelectedCompany._id;
    }

    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: 'col-12',
            type: 'autocomplete',
            key: 'company',
            templateOptions: {
              initialValues: preSelectedCompany
                ? [{
                  _id: preSelectedCompany._id,
                  name: preSelectedCompany.name,
                }]
                : undefined,
              customLabel: this.translateService.instant('properties.documents.summaryDocs.company'),
              placeholder: this.translateService.instant('properties.documents.summaryDocs.searchCompany'),
              bindLabel: 'name',
              bindValue: '_id',
              search$: this.companiesService.getList.bind(this),
              typeToSearchText: this.translateService.instant('common.words.typeToSearch', { minTermLength: 1 }),
              clearAllText: this.translateService.instant('common.words.clearAll'),
              notFoundText: this.translateService.instant('common.words.notFound'),
              loadingText: this.translateService.instant('common.words.loading'),
            },
            expressionProperties: {
              "templateOptions.onChange": (model: any) => {
                this.getCompanyData(model.company);
              },
            }
          },
        ]
      }
    ];
  }

  /**
   * Get current properties basing on selected company
   *
   * @since 1.0.0
   */
  getCompanyData(companyId: string): void {
    if (companyId === this.previousCompanyId) {
      return;
    }

    setTimeout(() => {
      this.dialogIsLoading = true;
      return;
    }, 50);
    // Get requested company data
    this.previousCompanyId = companyId;

    // Reset data if we receive a null id
    this.resetCompanyData();
    if (!companyId) {
      this.company = undefined;
      setTimeout(() => {
        this.dialogIsLoading = false;
        return;
      }, 500);
      return;
    }

    this.subscriptions.push(
      this.companiesService.get(companyId).subscribe(
        res => {
          if (res.status && res.data) {
            this.company = new Company();
            this.company.setData(res.data);
            if (this.isInit) {
              this.initCompanyForm(this.company);
              this.isInit = false;
            }
            if (res.data.subProperties && Array.isArray(res.data.subProperties)) {
              // Take only properties that are actually administrated by this company
              const newProperties = res.data.subProperties.filter(el => el.roles && el.roles.includes("admin"));
              this.filterProperties(newProperties);
              setTimeout(() => {
                this.dialogIsLoading = false;
                return;
              }, 500);
            } else {
              this.toastrService.error(this.translateService.instant("properties.documents.summaryDocs.no_properties"));
              setTimeout(() => {
                this.dialogIsLoading = false;
                return;
              }, 500);
            }
            return;
          }
        },
        err => {
          // if we came here company do not exists
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset current properties
   *
   * @since 1.0.0
   */
  resetCompanyData(): void {
    setTimeout(() => {
      this.dialogIsLoading = true;
      return;
    }, 50);
    this.subscriptions.push(
      this.summaryDocsService
        .getSummaryDocsProperties()
        .subscribe(res => {
          if (res.status) {
            this.initProperties(res.data.items);
            setTimeout(() => {
              this.dialogIsLoading = false;
              return;
            }, 500);
          } else {
            setTimeout(() => {
              this.dialogIsLoading = false;
              return;
            }, 500);
            this.toastrService.error(res.message);
          }
        })
    );
  }

  /**
   * Handle properties filtering basing on selected company
   *
   * @since 1.0.0
   */
  filterProperties(newProperties: any): void {
    if (!newProperties.length) {
      this.properties = [];
      return;
    }
    const newPropertiesIds = newProperties.map(el => el.propertyId);
    this.properties = this.properties.filter(el => newPropertiesIds.includes(el.id));
  }

  /**
   * Handle select all recipients
   *
   * @since 1.0.0
   */
  onSelectAll(checked: boolean): void {
    this.selectAll = checked;
    if (!checked) {
      this.properties.forEach((property: any) => {
        property["value"] = false;
      });
    } else if (checked) {
      this.properties.forEach((property: any) => {
        property["value"] = true;
      });
    }
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(selected?: boolean): void {
    if (!selected) {
      this.dialogRef.close();
    }
    const propertiesSelected = [];
    if (this.properties && Array.isArray(this.properties)) {
      this.properties.forEach((item: any) => {
        if (item.value) {
          propertiesSelected.push(item);
        }
      })
    }

    const data = {
      propertiesSelected,
      selectedCompany: this.model.company,
      selectAll: this.selectAll,
    }

    this.dialogRef.close(data);
  }
}
